/**
 * Coach Video Library Hook
 *
 * CRUD operations for the coach video library - reusable videos for
 * exercise demos, form feedback, technique instruction, etc.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";
import { toast } from "sonner";

// Types
export type VideoCategory =
  | "exercise_demo"
  | "form_feedback"
  | "technique"
  | "motivation"
  | "educational"
  | "other";

export interface CoachVideo {
  id: string;
  organization_id: string;
  coach_id: string;
  title: string;
  description: string | null;
  category: VideoCategory | null;
  tags: string[];
  storage_path: string;
  thumbnail_path: string | null;
  duration_seconds: number | null;
  file_size_bytes: number | null;
  is_public: boolean;
  view_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateVideoInput {
  title: string;
  description?: string;
  category?: VideoCategory;
  tags?: string[];
  storage_path: string;
  thumbnail_path?: string;
  duration_seconds?: number;
  file_size_bytes?: number;
  is_public?: boolean;
}

export interface UpdateVideoInput {
  title?: string;
  description?: string;
  category?: VideoCategory;
  tags?: string[];
  thumbnail_path?: string;
  is_public?: boolean;
}

export interface VideoFilters {
  category?: VideoCategory;
  tags?: string[];
  search?: string;
  isPublic?: boolean;
}

export interface UseCoachVideoLibraryReturn {
  videos: CoachVideo[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  fetchVideos: (filters?: VideoFilters) => Promise<void>;
  createVideo: (input: CreateVideoInput) => Promise<CoachVideo | null>;
  updateVideo: (id: string, input: UpdateVideoInput) => Promise<CoachVideo | null>;
  deleteVideo: (id: string) => Promise<boolean>;
  uploadVideo: (file: File, onProgress?: (percent: number) => void) => Promise<{ storagePath: string; thumbnailPath?: string } | null>;
  getVideoUrl: (storagePath: string) => Promise<string | null>;
  incrementViewCount: (id: string) => Promise<void>;
}

const VIDEO_CATEGORIES: { value: VideoCategory; label: string }[] = [
  { value: "exercise_demo", label: "Exercise Demo" },
  { value: "form_feedback", label: "Form Feedback" },
  { value: "technique", label: "Technique" },
  { value: "motivation", label: "Motivation" },
  { value: "educational", label: "Educational" },
  { value: "other", label: "Other" },
];

export { VIDEO_CATEGORIES };

export function useCoachVideoLibrary(): UseCoachVideoLibraryReturn {
  const { organization } = useOrganization();
  const [videos, setVideos] = useState<CoachVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchVideos = useCallback(
    async (filters?: VideoFilters) => {
      if (!organization?.id) return;

      setLoading(true);
      setError(null);

      try {
        let query = supabase
          .from("coach_video_library")
          .select("*", { count: "exact" })
          .eq("organization_id", organization.id)
          .order("created_at", { ascending: false });

        // Apply filters
        if (filters?.category) {
          query = query.eq("category", filters.category);
        }

        if (filters?.isPublic !== undefined) {
          query = query.eq("is_public", filters.isPublic);
        }

        if (filters?.tags && filters.tags.length > 0) {
          query = query.overlaps("tags", filters.tags);
        }

        if (filters?.search) {
          query = query.or(
            `title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
          );
        }

        const { data, error: fetchError, count } = await query;

        if (fetchError) throw fetchError;

        setVideos((data as CoachVideo[]) || []);
        setTotalCount(count || 0);
      } catch (err: any) {
        console.error("[useCoachVideoLibrary] Error fetching videos:", err);
        setError(err.message || "Failed to fetch videos");
      } finally {
        setLoading(false);
      }
    },
    [organization?.id]
  );

  // Initial fetch
  useEffect(() => {
    if (organization?.id) {
      fetchVideos();
    }
  }, [organization?.id, fetchVideos]);

  const uploadVideo = useCallback(
    async (
      file: File,
      onProgress?: (percent: number) => void
    ): Promise<{ storagePath: string; thumbnailPath?: string } | null> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) {
        toast.error("You must be logged in to upload videos");
        return null;
      }

      // Validate file type
      const validTypes = ["video/mp4", "video/webm", "video/quicktime", "video/x-msvideo"];
      if (!validTypes.includes(file.type)) {
        toast.error("Invalid file type. Please upload MP4, WebM, MOV, or AVI.");
        return null;
      }

      // Validate file size (100MB max)
      const maxSize = 100 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error("Video file is too large. Maximum size is 100MB.");
        return null;
      }

      try {
        // Generate unique filename
        const timestamp = Date.now();
        const ext = file.name.split(".").pop() || "mp4";
        const fileName = `${timestamp}-${Math.random().toString(36).substring(7)}.${ext}`;
        const storagePath = `${user.id}/${fileName}`;

        // Upload video
        const { error: uploadError } = await supabase.storage
          .from("coach-videos")
          .upload(storagePath, file, {
            cacheControl: "3600",
            upsert: false,
          });

        if (uploadError) throw uploadError;

        // Simulate progress (actual progress tracking requires custom implementation)
        onProgress?.(100);

        return { storagePath };
      } catch (err: any) {
        console.error("[useCoachVideoLibrary] Error uploading video:", err);
        toast.error(err.message || "Failed to upload video");
        return null;
      }
    },
    []
  );

  const createVideo = useCallback(
    async (input: CreateVideoInput): Promise<CoachVideo | null> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id || !organization?.id) {
        toast.error("You must be logged in to create videos");
        return null;
      }

      try {
        const { data, error: createError } = await supabase
          .from("coach_video_library")
          .insert({
            organization_id: organization.id,
            coach_id: user.id,
            title: input.title,
            description: input.description || null,
            category: input.category || null,
            tags: input.tags || [],
            storage_path: input.storage_path,
            thumbnail_path: input.thumbnail_path || null,
            duration_seconds: input.duration_seconds || null,
            file_size_bytes: input.file_size_bytes || null,
            is_public: input.is_public || false,
          })
          .select()
          .single();

        if (createError) throw createError;

        const newVideo = data as CoachVideo;
        setVideos((prev) => [newVideo, ...prev]);
        setTotalCount((prev) => prev + 1);
        toast.success("Video added to library");

        return newVideo;
      } catch (err: any) {
        console.error("[useCoachVideoLibrary] Error creating video:", err);
        toast.error(err.message || "Failed to add video to library");
        return null;
      }
    },
    [organization?.id]
  );

  const updateVideo = useCallback(
    async (id: string, input: UpdateVideoInput): Promise<CoachVideo | null> => {
      try {
        const { data, error: updateError } = await supabase
          .from("coach_video_library")
          .update({
            ...(input.title !== undefined && { title: input.title }),
            ...(input.description !== undefined && { description: input.description }),
            ...(input.category !== undefined && { category: input.category }),
            ...(input.tags !== undefined && { tags: input.tags }),
            ...(input.thumbnail_path !== undefined && { thumbnail_path: input.thumbnail_path }),
            ...(input.is_public !== undefined && { is_public: input.is_public }),
          })
          .eq("id", id)
          .select()
          .single();

        if (updateError) throw updateError;

        const updatedVideo = data as CoachVideo;
        setVideos((prev) =>
          prev.map((v) => (v.id === id ? updatedVideo : v))
        );
        toast.success("Video updated");

        return updatedVideo;
      } catch (err: any) {
        console.error("[useCoachVideoLibrary] Error updating video:", err);
        toast.error(err.message || "Failed to update video");
        return null;
      }
    },
    []
  );

  const deleteVideo = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        // Get video to delete storage file
        const videoToDelete = videos.find((v) => v.id === id);

        const { error: deleteError } = await supabase
          .from("coach_video_library")
          .delete()
          .eq("id", id);

        if (deleteError) throw deleteError;

        // Delete storage file if exists
        if (videoToDelete?.storage_path) {
          await supabase.storage
            .from("coach-videos")
            .remove([videoToDelete.storage_path]);
        }

        // Delete thumbnail if exists
        if (videoToDelete?.thumbnail_path) {
          await supabase.storage
            .from("coach-videos")
            .remove([videoToDelete.thumbnail_path]);
        }

        setVideos((prev) => prev.filter((v) => v.id !== id));
        setTotalCount((prev) => prev - 1);
        toast.success("Video deleted from library");

        return true;
      } catch (err: any) {
        console.error("[useCoachVideoLibrary] Error deleting video:", err);
        toast.error(err.message || "Failed to delete video");
        return false;
      }
    },
    [videos]
  );

  const getVideoUrl = useCallback(
    async (storagePath: string): Promise<string | null> => {
      try {
        const { data } = await supabase.storage
          .from("coach-videos")
          .createSignedUrl(storagePath, 3600); // 1 hour expiry

        return data?.signedUrl || null;
      } catch (err: any) {
        console.error("[useCoachVideoLibrary] Error getting video URL:", err);
        return null;
      }
    },
    []
  );

  const incrementViewCount = useCallback(async (id: string): Promise<void> => {
    try {
      // Use RPC or raw SQL for atomic increment
      await supabase.rpc("increment_video_view_count", { video_id: id });
    } catch (err: any) {
      // Non-critical error, just log it
      console.error("[useCoachVideoLibrary] Error incrementing view count:", err);
    }
  }, []);

  return {
    videos,
    loading,
    error,
    totalCount,
    fetchVideos,
    createVideo,
    updateVideo,
    deleteVideo,
    uploadVideo,
    getVideoUrl,
    incrementViewCount,
  };
}

export default useCoachVideoLibrary;
